import { Formik, FormikHelpers } from 'formik'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import FormLayout from 'components/v2/FormLayout'
import { IVehiclesV2FormStep1Values, IVehiclesV2FormValues } from 'features/Vehicles/types'
import { schema } from './schema'
import { PartnerSettingsForm } from 'features/Vehicles/components/PartnerSettingsForm'
import { useMutation } from '@apollo/client'
import {
  CHANGE_VEHICLE_CARDS,
  CHANGE_VEHICLE_DETAILS,
  CHANGE_VEHICLE_DRIVERS,
  CREATE_TVEHICLE
} from 'features/Vehicles/mutations-legacy'
import { getUnprocessableErrors } from '@10xlabs/utils'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { VehicleDetailsForm } from 'features/Vehicles/components/VehicleDetailsForm'
import { TemplateForm } from 'features/Vehicles/components/TemplateForm'
import { GTCreateTVehicleInput, GTCreateTVehicleResponse } from 'gql/types.generated'
import { CardsForm } from 'features/Vehicles/components/CardsForm'
import { useParams, useSearchParams } from 'react-router-dom'
import { getDifference } from 'utils/object'
import { isEmpty, trim } from 'lodash'
import { VehiclesV2FormUtils } from 'features/Vehicles/utils'
import { getDefaultCompaniesForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'

export const NewVehiclesV2Step1 = ({
  vehicle,
  isEdit,
  onSubmit,
  onCancel
}: {
  vehicle?: IVehiclesV2FormValues
  isEdit?: boolean
  onSubmit: ({ vehicleId }: { vehicleId: string }) => void
  onCancel: () => void
}): ReactElement => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const params = useParams()
  const login = useLogin()

  const [createTVehicle, { error: createVehicleError }] = useMutation<{ createTVehicle: GTCreateTVehicleResponse }>(
    CREATE_TVEHICLE
  )
  const [changeVehicleDetails, { error: changeVehicleDetailsError }] = useMutation(CHANGE_VEHICLE_DETAILS)
  const [changeVehicleCards, { error: changeVehicleCardsError }] = useMutation(CHANGE_VEHICLE_CARDS)
  const [changeVehicleDrivers, { error: changeVehicleDriversError }] = useMutation(CHANGE_VEHICLE_DRIVERS)
  const handleCreateSubmit = async (
    values: IVehiclesV2FormStep1Values,
    { setSubmitting }: FormikHelpers<IVehiclesV2FormStep1Values>
  ): Promise<void> => {
    try {
      const input: GTCreateTVehicleInput = {
        externalID: values.externalID ? trim(values.externalID) : null,
        partnerID: values.partner.id,
        driverIDs: values.drivers.map(({ id }) => id),
        companyIDs: values.companies.map(({ id }) => id),
        name: trim(values.name),
        vin: values.vin,
        cityName: values.cityName || null,
        stateCode: values.state?.code || null,
        templateID: values.template.id,
        cards: {
          tagCards: values.tagCards,
          fuelCards: values.fuelCards
        }
      }
      const { data } = await createTVehicle({
        variables: {
          input
        }
      })
      const vehicleId = data?.createTVehicle.id as string
      onSubmit({ vehicleId })
    } catch (ex) {
      // DO NOTHING
    }
    setSubmitting(false)
  }

  const handleEditSubmit = async (
    values: IVehiclesV2FormStep1Values,
    { setSubmitting }: FormikHelpers<IVehiclesV2FormStep1Values>
  ): Promise<void> => {
    try {
      const oldVehicleDetails = VehiclesV2FormUtils.mapVehicleDetailsFormToMutation(
        vehicle as IVehiclesV2FormStep1Values
      )
      const newVehicleDetails = VehiclesV2FormUtils.mapVehicleDetailsFormToMutation(values)
      const vehicleDetailsDiff = getDifference(newVehicleDetails, oldVehicleDetails)
      if (!isEmpty(vehicleDetailsDiff)) {
        await changeVehicleDetails({
          variables: {
            id: vehicleId,
            input: newVehicleDetails
          }
        })
      }

      const oldVehicleCards = VehiclesV2FormUtils.mapVehicleCardsFormToMutation(vehicle as IVehiclesV2FormStep1Values)
      const newVehicleCards = VehiclesV2FormUtils.mapVehicleCardsFormToMutation(values)
      const vehicleCardsDiff = getDifference(newVehicleCards, oldVehicleCards)

      if (!isEmpty(vehicleCardsDiff)) {
        await changeVehicleCards({
          variables: {
            id: vehicleId,
            input: newVehicleCards
          }
        })
      }

      const oldDriversIDs = vehicle?.drivers.map(({ id }) => id)
      const newDriversIDs = values.drivers.map(({ id }) => id)
      const driversDiff = getDifference(newDriversIDs, oldDriversIDs)
      if (!isEmpty(driversDiff)) {
        await changeVehicleDrivers({
          variables: {
            id: vehicleId,
            input: { driverIDs: newDriversIDs }
          }
        })
      }
      onSubmit({ vehicleId: vehicleId as string })
    } catch (ex) {
      // DO NOTHING
    }
    setSubmitting(false)
  }

  const initialValues: IVehiclesV2FormStep1Values = {
    externalID: vehicle?.externalID || '',
    partner: vehicle?.partner || { name: '', id: '' },
    drivers: vehicle?.drivers || [],
    companies: vehicle?.companies || getDefaultCompaniesForForm(login.accessToken),
    name: vehicle?.name || '',
    vin: vehicle?.vin || '',
    cityName: vehicle?.cityName || '',
    state: vehicle?.state ? vehicle?.state : null,
    template: {
      id: vehicle?.template.id || '',
      type: vehicle?.template.type || '',
      capacity: vehicle?.template.capacity || ''
    },
    tagCards: vehicle?.tagCards || [],
    fuelCards: vehicle?.fuelCards || []
  }

  const vehicleId = params.vehicleId || searchParams.get('vehicleId')
  const isCreating = !isEdit && !vehicleId

  return (
    <Formik
      onSubmit={isCreating ? handleCreateSubmit : handleEditSubmit}
      initialValues={initialValues}
      validationSchema={schema(intl)}
    >
      {({ handleSubmit, dirty, isSubmitting }): ReactElement => {
        const isEditing = !isCreating && dirty

        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <FormLayout.Card small>
              <PartnerSettingsForm isEdit={!isCreating} />
            </FormLayout.Card>
            <FormLayout.Card small>
              <VehicleDetailsForm initialCompanies={vehicle?.companies || []} />
            </FormLayout.Card>
            <FormLayout.Card small>
              <TemplateForm isEdit={!isCreating} />
            </FormLayout.Card>
            <FormLayout.Card small>
              <CardsForm />
            </FormLayout.Card>
            {createVehicleError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(createVehicleError)}>
                  <FormattedMessage
                    id="vehicles.form.create.error"
                    defaultMessage="There was an error while creating the vehicle"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changeVehicleDetailsError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeVehicleDetailsError)}>
                  <FormattedMessage
                    id="vehicles.form.details.error"
                    defaultMessage="There was an error while updating the vehicle details"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changeVehicleCardsError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeVehicleCardsError)}>
                  <FormattedMessage
                    id="vehicles.form.cards.error"
                    defaultMessage="There was an error while updating the vehicle cards"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changeVehicleDriversError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeVehicleDriversError)}>
                  <FormattedMessage
                    id="vehicles.form.drivers.error"
                    defaultMessage="There was an error while updating the vehicle drivers"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" loading={isSubmitting}>
                {isCreating && (
                  <FormattedMessage id="partners.new.step1.createAndContinue" defaultMessage="Create & Continue" />
                )}
                {isEditing && (
                  <FormattedMessage id="partners.new.step1.saveAndContinue" defaultMessage="Save & Continue" />
                )}
                {!isCreating && !isEditing && (
                  <FormattedMessage id="partners.new.step1.Continue" defaultMessage="Continue" />
                )}
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
